/**
 * Issue #385 — 质检中心批量上传录音最大文件数（语音识别API）纯逻辑 + 文案常量。
 *
 * admin vitest 无路由 / 组件渲染夹具（UI 行为由 Playwright e2e 覆盖），因此把
 * 「能否保存 / 区间校验 / 钳制 / 文案」抽成纯函数：MaxBatchUploadSettingsCard 仅做
 * 薄包装并复用本模块，关键决策（尤其是「只读角色绝不触发保存」）由单测锁定，
 * 杜绝纯前端假状态。区间 / 默认值以后端下发为准（1-50，默认 10）。
 */
export const MAX_BATCH_UPLOAD_LABEL = '批量上传最大文件数';

/** 兜底默认（仅在后端尚未下发时占位；正式取值始终以接口返回为准）。 */
export const MAX_BATCH_UPLOAD_DEFAULT = 10;
export const MAX_BATCH_UPLOAD_MIN = 1;
export const MAX_BATCH_UPLOAD_MAX = 50;

export interface MaxBatchUploadRange {
  min: number;
  max: number;
  defaultMaxBatchUploadFiles: number;
}

/** 接口缺失时使用的兜底区间（与后端常量一致）。 */
export const DEFAULT_MAX_BATCH_UPLOAD_RANGE: MaxBatchUploadRange = {
  min: MAX_BATCH_UPLOAD_MIN,
  max: MAX_BATCH_UPLOAD_MAX,
  defaultMaxBatchUploadFiles: MAX_BATCH_UPLOAD_DEFAULT,
};

/** 草稿字符串 → 是否为合法整数且落在区间内（非数字 / 小数 / 越界一律判否）。 */
export function isMaxBatchUploadInRange(raw: string, range: MaxBatchUploadRange): boolean {
  const trimmed = raw.trim();
  if (trimmed === '') return false;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return false;
  return n >= range.min && n <= range.max;
}

/** 把任意输入钳制到合法区间整数（与后端 clampQcMaxBatchUploadFiles 同语义，用于前端展示兜底）。 */
export function clampMaxBatchUpload(value: number, range: MaxBatchUploadRange): number {
  if (!Number.isFinite(value)) return range.defaultMaxBatchUploadFiles;
  const intVal = Math.round(value);
  if (intVal < range.min) return range.min;
  if (intVal > range.max) return range.max;
  return intVal;
}

/**
 * 能否保存：必须可编辑（ADMIN/SUPER_ADMIN）、当前未在保存中、草稿在合法区间。
 * `canEdit=false`（只读 / 非编辑角色）永远返回 false —— 卡片据此禁用输入并隐藏保存按钮，
 * 任何只读路径都不会触发真实 PATCH。
 */
export function canSaveMaxBatchUpload(args: {
  canEdit: boolean;
  pending: boolean;
  raw: string;
  range: MaxBatchUploadRange;
}): boolean {
  if (!args.canEdit) return false;
  if (args.pending) return false;
  return isMaxBatchUploadInRange(args.raw, args.range);
}

/** 合法区间的中文提示文案（展示当前允许范围与默认值）。 */
export function maxBatchUploadRangeHint(range: MaxBatchUploadRange): string {
  return `合法区间 ${range.min}-${range.max} 个，默认 ${range.defaultMaxBatchUploadFiles} 个`;
}
